import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import {
  Title as TitleComponent,
  Description as DescriptionComponent,
  BaseLinkStyle,
  BaseWhiteLinkStyle,
} from './ContentSlice';

import { getCmsIconReplacement } from './benefit-icons';

import { transformProperties, fieldHasContent } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint } from '@/src/domains/shared/breakpoints';

const IllustrationWrapper = styled.div`
  width: 70px;
  height: 70px;
  background-color: var(--newBrandColor);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 15px;
  padding: 20px;
  box-sizing: border-box;
  color: var(--colors-blank);

  ${tabletBreakpoint(css`
    margin-bottom: 30px;
  `)};

  img {
    max-height: 30px;
  }

  svg {
    width: 30px;
    height: 30px;
  }
`;

export const Title = styled(TitleComponent)`
  ${fluidProp('font-size', '28px', '42px')}
  padding-left: 30px;
  margin-bottom: 50px;
`;

const Benefit = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: flex-start;
  margin-bottom: 29px;

  ${tabletBreakpoint(css`
    flex-direction: column;
    text-align: center;
    align-items: center;
  `)};
`;

const Description = styled(DescriptionComponent)`
  && {
    width: 80%;
    padding-top: 0;

    > p {
      margin-top: 0;
    }
  }
`;

const Section = styled.section`
  ${fluidProp(['padding-top', 'padding-bottom'], '70px', '138px')}
  font-family: var(--typography-fontBrand);

  ${({ $tone }) => {
    switch ($tone) {
      case true: {
        return css`
          background-color: var(--newBrandColor);
          ${BaseWhiteLinkStyle}

          ${IllustrationWrapper} {
            background-color: var(--colors-blank);
            color: var(--newBrandColor);
          }

          ${Title}, ${Description} {
            color: var(--colors-blank);
          }
        `;
      }
      case false:
      default:
        return css`
          background: var(--colors-blank);
          ${BaseLinkStyle}
        `;
    }
  }}
`;

export function BenefitsSlice({ slice }) {
  const { primary } = transformProperties(slice);

  const { title, tone, sectionId } = primary;
  const { fields: benefits } = slice;

  return (
    <Section id={sectionId} $tone={tone}>
      <ContentContainer>
        {fieldHasContent(title) && (
          <Title>
            <PrismicRichText field={title} />
          </Title>
        )}
        {benefits.map(({ description, icon }, index) => {
          const NormaIcon = getCmsIconReplacement(icon?.url);

          return (
            <Benefit key={index}>
              {icon && (
                <IllustrationWrapper>
                  {NormaIcon ? (
                    <NormaIcon />
                  ) : (
                    <Image
                      src={icon?.url}
                      alt={icon?.alt || ''}
                      width={icon?.dimensions.width}
                      height={icon?.dimensions.height}
                      quality="100"
                      loading="eager"
                    />
                  )}
                </IllustrationWrapper>
              )}

              {fieldHasContent(description) && (
                <Description>
                  <PrismicRichText field={description} />
                </Description>
              )}
            </Benefit>
          );
        })}
      </ContentContainer>
    </Section>
  );
}
